<template>
  <div v-if="$page && theme" :key="theme.id" class="page">
    <header class="header -column -wide">
      <div class="theme__nav">
        <nav>
          <RouterLink v-reveal :to="{ name: 'themes' }" class="header__breadcrumb">
            {{ $page.labels.title }}
          </RouterLink>

          <RouterLink
            v-if="!isParentTheme"
            v-reveal="{ delay: 75 }"
            :to="{ name: 'theme', params: { themeSlug: parentTheme.slug } }"
            class="header__breadcrumb"
          >
            {{ parentTheme.title }}
          </RouterLink>

          <span v-reveal="{ delay: 150 }" class="header__breadcrumb -current">{{
            theme.title
          }}</span>
        </nav>

        <h1 v-reveal="{ delay: 600 }" class="header__title theme__nav__title">{{ theme.title }}</h1>
      </div>
    </header>

    <section v-reveal="{ delay: 800 }" class="section -mid-top row">
      <div class="section__content theme">
        <RawHtml class="row__item -span-9 -flower" :html="theme.content" />

        <nav class="row__item -span-3 theme__aside">
          <button
            @click="scrollToTools"
            class="button -link -flat -corner-right theme__aside-button"
            v-if="tools.length"
          >
            {{ $page.labels.view_tools }}
          </button>
          <template v-if="childThemeList.length">
            <h2 class="section__subtitle -sm">
              {{
                childThemeSlug
                  ? 'Thématique principale et autre sous-thématiques'
                  : $page.labels.sub_themes
              }}
            </h2>

            <RouterLink
              v-if="childThemeSlug"
              :to="getParentThemeLink()"
              class="theme__child -parent"
            >
              {{ parentTheme.title }}
              <Icon id="leaves" class="theme__aside-leaves" />
            </RouterLink>

            <RouterLink
              v-for="child in childThemeList"
              :key="child.id"
              :to="getChildThemeLink(child)"
              class="theme__child"
            >
              {{ child.title }}
              <Icon id="leaves" class="theme__aside-leaves" />
            </RouterLink>
          </template>
        </nav>
      </div>
    </section>

    <section id="outils" class="section -mid-top" ref="tools" v-if="tools.length">
      <h2 class="section__title">{{ $page.labels.associated_tools }}</h2>
      <div v-if="tools.length" class="theme__tools">
        <ul v-reveal="{ delay: 550 }" class="theme__tools__list">
          <li
            v-for="(item, idx) in firstTools"
            :key="item.id"
            v-reveal="{ delay: idx * 75 }"
            class="card -training"
          >
            <a :href="getHref(item)" class="row" target="_blank" rel="noopener">
              <span
                class="bubble -tool theme__tools__item-icon"
                :style="{ backgroundColor: getToolColor(item) }"
                ><Icon :id="getToolIcon(item)"
              /></span>
              <div class="card__content">
                <div class="row -spread -align-start">
                  <h2 class="card__title">{{ item.title }}</h2>
                  <div class="card__medias__link link -after">
                    <span>Consulter </span>
                    <Icon id="leaves" />
                  </div>
                </div>
                <p>{{ item.description }}</p>
              </div>
            </a>
          </li>
        </ul>

        <DropDown :is-open="isToolsListOpened">
          <ul v-reveal="{ delay: 550 }" class="theme__tools__list">
            <li v-for="item in toolsList" :key="item.id" class="card -training">
              <a :href="getHref(item)" class="row" target="_blank" rel="noopener">
                <span
                  class="bubble -tool theme__tools__item-icon"
                  :style="{ backgroundColor: getToolColor(item) }"
                  ><Icon :id="getToolIcon(item)"
                /></span>
                <div class="card__content">
                  <div class="row -spread -align-start">
                    <h2 class="card__title">{{ item.title }}</h2>
                    <div class="card__medias__link link -after">
                      <span>Consulter </span>
                      <Icon id="leaves" />
                    </div>
                  </div>
                  <p>{{ item.description }}</p>
                </div>
              </a>
            </li>
          </ul>
        </DropDown>

        <button
          v-if="isToggleButtonVisible"
          v-reveal="{ delay: 600 }"
          class="button -alt -corner-left theme__tools__more"
          @click="toggleToolsList"
        >
          <span class="theme__tools__more-icon"> {{ isToolsListOpened ? '-' : '+' }} </span>
          {{ $page.labels.more_tools }}
        </button>
      </div>
    </section>

    <section v-if="trainings.length" class="section -mid-top theme__trainings">
      <h2 v-reveal="{ delay: 100 }" class="section__title">
        {{ $page.labels.associated_trainings }}
      </h2>

      <ul class="row -mobile theme__trainings__list">
        <TrainingCard
          v-for="(item, idx) in trainings"
          :key="item.key"
          theme
          v-reveal="{ delay: idx * 75 }"
          :training="item"
          class="theme__trainings__item"
          :labels="{ ended: 'Terminé', info: 'Plus d’info', offered_by: 'Offert par' }"
        />
      </ul>
    </section>

    <CtaContactSection
      v-reveal="{ delay: 100 }"
      :title="contactTitle"
      :subtitle="contactDescription"
      :link-label="contactLink"
      :to="contactTo"
    />
  </div>
</template>

<script>
import TrainingCard from '@/components/cards/TrainingCard.vue'
import DropDown from '@/components/global/DropDown.vue'
import Icon from '@/components/global/Icon.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import CtaContactSection from '@/components/sections/CtaContactSection.vue'
import { supportedExtension } from '@/constants'
import { setMeta } from '@/helpers/metaHelpers.js'

export default {
  name: 'Theme',
  components: { DropDown, CtaContactSection, TrainingCard, Icon, RawHtml },
  beetPage: 'theme',
  props: {
    themeSlug: { type: String, required: true },
    childThemeSlug: { type: String, default: null },
  },
  data() {
    return {
      isToggleButtonVisible: true,
      isToolsListOpened: false,
    }
  },
  computed: {
    contactDescription() {
      return this.theme.cta ? this.theme.cta.description : this.$page.contact.description
    },
    contactTitle() {
      return this.theme.cta ? this.theme.cta.title : this.$page.contact.title
    },
    contactLink() {
      return this.theme.cta
        ? this.theme.cta.label
          ? this.theme.cta.label
          : this.$page.contact.label
        : this.$page.contact.label
    },
    contactTo() {
      return this.theme.cta
        ? this.theme.cta.link
          ? this.theme.cta.link
          : this.$page.contact.link
        : this.$page.contact.link
    },
    parentTheme() {
      return Object.values(this.$store.getters.themes).find(({ slug }) =>
        slug.includes(this.themeSlug),
      )
    },
    theme() {
      const theme = Object.values(this.$store.getters.themes).find(({ slug }) =>
        slug.includes(this.themeSlug),
      )
      if (this.childThemeSlug) {
        return theme.children.find(({ slug }) => slug.includes(this.childThemeSlug))
      }
      return theme
    },
    childThemeList() {
      if (this.childThemeSlug) {
        return [...this.parentTheme.children].toSorted((a, b) => {
          if (a.slug === this.childThemeSlug) return -1
          else if (b.slug === this.childThemeSlug) return 1
          return 0
        })
      }
      return this.parentTheme.children
    },
    isParentTheme() {
      return !this.childThemeSlug
    },
    relatedThemes() {
      const { relatedThemes = [] } = this.theme || {}
      return relatedThemes.map((themeId) => this.$store.getters.themes[themeId])
    },
    tools() {
      const { tools = [] } = this.theme || {}
      const relatedTools = tools
        .map((toolId) => this.$beet.tools[toolId])
        .toSorted((a, b) => a.title[0].localeCompare(b.title[0]))
      this.setToggle(relatedTools.length)
      return relatedTools
    },
    toolsList() {
      return this.tools.slice(3, this.tools.length)
    },
    firstTools() {
      return this.tools.slice(0, 3)
    },
    trainings() {
      const { trainings = [] } = this.theme || {}
      const now = new Date()
      return trainings
        .map((trainingId) => this.$beet.trainings[trainingId])
        .map((training) =>
          Object.assign(
            { ended: training.end_date && now > new Date(training.end_date) },
            training,
          ),
        )
    },
  },
  beforeMount() {
    if (!this.theme) this.$router.replace({ name: 'not-found' })
  },
  methods: {
    getHref(item) {
      return item.external ? item.url : item.file.original
    },
    getToolIcon(item) {
      if (!item.external || item.is_file) {
        const extension = item.is_file
          ? item.url.split('.').pop()
          : item.file.original.split('.').pop()
        return supportedExtension.indexOf(extension) !== -1 ? `tool-${extension}` : 'tool-doc'
      }
      return 'tool-link'
    },
    getToolColor(item) {
      if (item.external) return '#007358'
      const extension = item.is_file
        ? item.url.split('.').pop()
        : item.file.original.split('.').pop()
      if (extension === 'pdf') return '#0BBBA4'
      else return '#FF6E5F'
    },
    setToggle(toolCount) {
      this.isToggleButtonVisible = !(toolCount < 4 || !toolCount)
    },
    toggleToolsList() {
      this.isToolsListOpened = !this.isToolsListOpened
    },
    getChildThemeLink({ slug }) {
      return { ...this.$route, params: { childThemeSlug: slug }, query: '' }
    },
    getParentThemeLink() {
      return { ...this.$route, params: { themeSlug: this.parentTheme.slug }, query: '' }
    },
    scrollToTools() {
      const tools = this.$refs['tools']
      tools.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
  },
  watch: {
    $page: {
      handler(page) {
        if (page && this.theme) {
          const title = this.theme.seo?.title || this.theme.title

          setMeta({
            title: `${title} | Boîte-à-outils`,
            description: this.theme.seo?.description || page.seo.description,
            image: this.theme.seo?.image || this.theme.image,
          })
        }
      },
      immediate: true,
    },
  },
}
</script>
